import React, { useMemo, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { LifeBuoy } from 'lucide-react';
import { useAppSelector } from '../../store';
import { HELP_CATEGORIES, HELP_TOPICS } from '../../lib/help/helpTopics';
import { trackHelpEvent } from '../../lib/help/helpAnalytics';
import { TabPageShell, TabPageHeader } from '../common/TabPageShell';

const HelpTab: React.FC = () => {
  const { t } = useTranslation();
  const context = useAppSelector((state) => state.help.lastHelpContext);
  const [activeCategory, setActiveCategory] = useState<(typeof HELP_CATEGORIES)[number]['id']>('getting_started');
  const [query, setQuery] = useState('');

  const tr = (key: string, defaultValue: string, options?: Record<string, unknown>): string => {
    const translated = t(key, { ...(options ?? {}), defaultValue });
    return translated === key ? defaultValue : translated;
  };

  useEffect(() => {
    trackHelpEvent('help_center_opened', { context, source: 'help_tab' });
  }, [context]);

  const translateTopic = (topic: (typeof HELP_TOPICS)[number]) => {
    const title = tr(`help.topics.${topic.id}.title`, topic.title);
    const summary = tr(`help.topics.${topic.id}.summary`, topic.summary);
    const bodyRaw = t(`help.topics.${topic.id}.body`, {
      returnObjects: true,
      defaultValue: topic.body,
    });
    const body = Array.isArray(bodyRaw) && bodyRaw.every((l) => typeof l === 'string')
      ? (bodyRaw as string[])
      : topic.body;
    return { title, summary, body };
  };

  const filteredTopics = useMemo(() => {
    const lowered = query.trim().toLowerCase();
    return HELP_TOPICS.filter((topic) => {
      const byCategory = topic.category === activeCategory;
      if (!byCategory) return false;
      if (!lowered) return true;
      const translated = translateTopic(topic);
      return (
        translated.title.toLowerCase().includes(lowered) ||
        translated.summary.toLowerCase().includes(lowered) ||
        translated.body.some((line) => line.toLowerCase().includes(lowered)) ||
        topic.title.toLowerCase().includes(lowered) ||
        topic.summary.toLowerCase().includes(lowered) ||
        topic.body.some((line) => line.toLowerCase().includes(lowered))
      );
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCategory, query, t]);

  return (
    <TabPageShell className="animate-in h-full min-h-0 overflow-auto">
      <TabPageHeader
        title={tr('help.title', 'Help Center')}
        subtitle={tr('help.contextDescription', `Context: ${context}. Find quick guidance, workflows, and support links.`, {
          context,
        })}
        icon={<LifeBuoy className="h-5 w-5 text-accent-primary" aria-hidden="true" />}
      />

      <div className="grid gap-4 lg:grid-cols-[220px_minmax(0,1fr)]">
        <aside className="space-y-2">
          <label className="sr-only" htmlFor="help-tab-search">
            {tr('help.searchTopicsLabel', 'Search help topics')}
          </label>
          <input
            id="help-tab-search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            className="w-full rounded-lg bg-surface-container-high px-3 py-2 text-sm text-text-primary"
            placeholder={tr('help.searchPlaceholder', 'Search help...')}
          />

          <nav className="space-y-1" aria-label={tr('help.categoriesLabel', 'Help categories')}>
            {HELP_CATEGORIES.map((category) => (
              <button
                key={category.id}
                type="button"
                onClick={() => {
                  setActiveCategory(category.id);
                  trackHelpEvent('help_topic_viewed', {
                    context,
                    category: category.id,
                    source: 'help_tab_category',
                  });
                }}
                className={`inline-flex w-full min-h-[44px] items-center rounded-md px-3 py-2 text-left text-sm ${
                  activeCategory === category.id
                    ? 'bg-accent-primary/20 text-accent-primary'
                    : 'text-text-secondary hover:bg-surface-container-low'
                }`}
              >
                {tr(`help.categories.${category.id}`, category.label)}
              </button>
            ))}
          </nav>
        </aside>

        <section className="space-y-3">
          {filteredTopics.length === 0 ? (
            <div className="rounded-xl bg-surface-container-high p-4 text-sm text-text-secondary">
              {tr('help.noTopics', 'No topics match your search in this category.')}
            </div>
          ) : (
            filteredTopics.map((topic) => {
              const translated = translateTopic(topic);
              return (
              <article key={topic.id} className="rounded-xl bg-surface-container-high p-4">
                <h3 className="mb-1 text-base font-semibold text-text-primary break-words">{translated.title}</h3>
                <p className="mb-3 text-sm text-text-secondary break-words">{translated.summary}</p>
                <ul className="list-disc space-y-1 ltr:pl-5 rtl:pr-5 text-sm text-text-secondary">
                  {translated.body.map((line, idx) => (
                    <li key={`${topic.id}-${idx}`} className="break-words">{line}</li>
                  ))}
                </ul>
                {topic.quickActions?.length ? (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {topic.quickActions.map((action) =>
                      action.href ? (
                        <button
                          key={action.label}
                          type="button"
                          className="rounded-md bg-surface-container px-3 py-1.5 text-xs text-text-primary hover:bg-surface-container-high"
                          onClick={() => {
                            window.open(action.href, '_blank', 'noopener,noreferrer');
                            trackHelpEvent('external_help_opened', {
                              context,
                              source: 'help_tab_topic_action',
                              topicId: topic.id,
                            });
                          }}
                        >
                          {action.label}
                        </button>
                      ) : null
                    )}
                  </div>
                ) : null}
              </article>
              );
            })
          )}
        </section>
      </div>
    </TabPageShell>
  );
};

export default HelpTab;
